import { useEffect, useState } from "react";
import { getPublishedTestimonials, getPortfolio, getServices, getHero, getAbout, getGlobals, getSectionHeaders, getNavigation, getFormServices, getContactMethods, getUiStrings, getEmailProcesses, getFAQs, getFaqQuestions, getSettings } from "../lib/api";
import Header from "../components/site/Header";
import Hero from "../components/site/Hero";
import ServicesHub from "../components/site/ServicesHub";
import About from "../components/site/About";
import Portfolio from "../components/site/Portfolio";
import Testimonials from "../components/site/Testimonials";
import Faq from "../components/site/Faq";
import Contact from "../components/site/Contact";
import Footer from "../components/site/Footer";

function applySiteData(d, setters) {
  setters.setWhatsapp(d.globals?.whatsapp_number || "");
  setters.setPortfolioItems((d.portfolio || []).filter(p => p.published !== false));
  setters.setTestimonialItems(d.testimonials || []);
  setters.setHeroContent(d.hero);
  setters.setAboutContent(d.about);
  setters.setGlobals(d.globals);
  setters.setSectionHeaders(d.sectionHeaders);
  setters.setNavigation(d.navigation);
  setters.setFormServices(d.formServices);
  setters.setContactMethods(d.contactMethods);
  setters.setUiStrings(d.uiStrings);

  const manualFaqs = d.faqs || [];
  const publishedQuestions = (d.faqQuestions || [])
    .filter(q => q.status === "published" && q.answer)
    .map(q => ({ id: q.id, question: q.question, answer: q.answer, sort_order: 99 }));
  setters.setFaqItems([...manualFaqs, ...publishedQuestions]);

  const s = d.settings || {};
  setters.setEnabledSections({
    hero: s.section_hero_visible !== "false",
    about: s.section_about_visible !== "false",
    services: s.section_services_visible !== "false",
    portfolio: s.section_portfolio_visible !== "false",
    testimonials: s.section_testimonials_visible !== "false",
    faq: s.section_faq_visible !== "false",
    contact: s.section_contact_visible !== "false",
  });

  setters.setElementVisibility({
    hero_cta: s.element_hero_cta_visible !== "false",
    hero_scroll: s.element_hero_scroll_visible !== "false",
    about_image: s.element_about_image_visible !== "false",
    about_stats: s.element_about_stats_visible !== "false",
    services_details: s.element_services_details_visible !== "false",
    services_carousel: s.element_services_carousel_visible !== "false",
    contact_info: s.element_contact_info_visible !== "false",
    contact_form: s.element_contact_form_visible !== "false",
    faq_form: s.element_faq_form_visible !== "false",
    faq_items: s.element_faq_items_visible !== "false",
  });

  const ogTitle = s.og_title || "Hub Profissional — Emilly Prates";
  const ogDesc = s.og_description || "Consultoria financeira, Gestão de Patrimônio e Design";
  const ogImg = s.og_image || "";
  const ogUrl = s.og_url || "";
  function setMeta(prop, content) {
    let el = document.querySelector(`meta[property="${prop}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
    el.setAttribute("content", content);
  }
  setMeta("og:title", ogTitle);
  setMeta("og:description", ogDesc);
  setMeta("og:image", ogImg);
  setMeta("og:url", ogUrl);

  const displayServices = (d.services || [])
    .filter(s => s.published === true)
    .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  setters.setServicesData(displayServices);
}

export default function Landing() {
  const [loading, setLoading] = useState(true);
  const [whatsapp, setWhatsapp] = useState("");
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [testimonialItems, setTestimonialItems] = useState([]);
  const [heroContent, setHeroContent] = useState(null);
  const [aboutContent, setAboutContent] = useState(null);
  const [servicesData, setServicesData] = useState([]);
  const [globals, setGlobals] = useState(null);
  const [sectionHeaders, setSectionHeaders] = useState(null);
  const [navigation, setNavigation] = useState(null);
  const [formServices, setFormServices] = useState(null);
  const [contactMethods, setContactMethods] = useState(null);
  const [uiStrings, setUiStrings] = useState(null);
  const [faqItems, setFaqItems] = useState([]);
  const [enabledSections, setEnabledSections] = useState(null);
  const [elementVisibility, setElementVisibility] = useState(null);

  useEffect(() => {
    async function loadAllData() {
      try {
        const isProd = window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1";

        if (isProd) {
          const res = await fetch("/api/site-data");
          if (!res.ok) throw new Error("Failed to fetch site data");
          const data = await res.json();
          applySiteData(data, { setWhatsapp, setPortfolioItems, setTestimonialItems, setHeroContent, setAboutContent, setGlobals, setSectionHeaders, setNavigation, setFormServices, setContactMethods, setUiStrings, setFaqItems, setEnabledSections, setElementVisibility, setServicesData });
        } else {
          const [
            globalsData,
            portfolio,
            testimonials,
            hero,
            about,
            services,
            headersData,
            navData,
            formServicesData,
            contactMethodsData,
            uiStringsData,
            processesData,
            faqData,
            faqQuestionsData,
            settingsData
          ] = await Promise.all([
            getGlobals(),
            getPortfolio(),
            getPublishedTestimonials(),
            getHero(),
            getAbout(),
            getServices(),
            getSectionHeaders(),
            getNavigation(),
            getFormServices(),
            getContactMethods(),
            getUiStrings(),
            getEmailProcesses(),
            getFAQs(),
            getFaqQuestions(),
            getSettings()
          ]);

          applySiteData({
            globals: globalsData,
            portfolio,
            testimonials,
            hero,
            about,
            services,
            sectionHeaders: headersData,
            navigation: navData,
            formServices: formServicesData,
            contactMethods: contactMethodsData,
            uiStrings: uiStringsData,
            faqs: faqData,
            faqQuestions: faqQuestionsData,
            settings: settingsData,
          }, { setWhatsapp, setPortfolioItems, setTestimonialItems, setHeroContent, setAboutContent, setGlobals, setSectionHeaders, setNavigation, setFormServices, setContactMethods, setUiStrings, setFaqItems, setEnabledSections, setElementVisibility, setServicesData });
        }
      } catch (err) {
        console.error("Error loading site data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadAllData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-offwhite flex items-center justify-center">
        <div className="font-serif text-2xl animate-pulse text-softblack">Hub<span className="text-champagne">.</span></div>
      </div>
    );
  }

  return (
    <div className="App bg-offwhite">
      <Header navigation={navigation} globals={globals} hasPortfolio={portfolioItems.length > 0} hasTestimonials={testimonialItems.length > 0} enabledSections={enabledSections} />
      <main>
        {enabledSections?.hero !== false && <Hero whatsappNumber={whatsapp} content={heroContent} uiStrings={uiStrings} elementVisibility={elementVisibility} />}
        {enabledSections?.services !== false && (
          <ServicesHub 
            services={servicesData} 
            sectionContent={sectionHeaders?.services} 
            uiStrings={uiStrings?.general}
            globals={globals}
            elementVisibility={elementVisibility}
          />
        )}
        {enabledSections?.about !== false && <About content={aboutContent} uiStrings={uiStrings?.general} elementVisibility={elementVisibility} />}
        {enabledSections?.portfolio !== false && portfolioItems.length > 0 && (
          <Portfolio 
            items={portfolioItems} 
            sectionContent={sectionHeaders?.portfolio} 
            uiStrings={uiStrings?.general}
          />
        )}
        {enabledSections?.testimonials !== false && testimonialItems.length > 0 && (
          <Testimonials items={testimonialItems} sectionContent={sectionHeaders?.testimonials} />
        )}
        {enabledSections?.faq !== false && <Faq items={faqItems} sectionContent={sectionHeaders?.faq} elementVisibility={elementVisibility} />}
        {enabledSections?.contact !== false && (
          <Contact 
            whatsappNumber={whatsapp} 
            sectionContent={sectionHeaders?.contact} 
            formServices={formServices}
            contactMethods={contactMethods}
            globals={globals}
            uiStrings={uiStrings}
            elementVisibility={elementVisibility}
          />
        )}
      </main>
        <Footer globals={globals} whatsappNumber={whatsapp} navigation={navigation} uiStrings={uiStrings} enabledSections={enabledSections} />
    </div>
  );
}
console.log('TEST BUILD FORCE')
